import { TEST_ID_WATCHLIST } from "e2e/testId";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  PlusIcon,
  SettingsIcon,
} from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import PopoverMenuView from "./PopoverMenuView";

import type { MobileActionMenuItem } from "@/components/molecules/MobileActionMenu";

// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/button) —…
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";

import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Props for PopoverMenuContainer component
 */
interface Props {
  selectedWatchlist?: WatchlistWatchlistMetaFragment | null;
  watchlists: WatchlistWatchlistMetaFragment[];
  onSelect: (value: string) => void;
  onCallbackOpen?: () => void;
  onOpenMyRoster?: () => void;
}

/**
 * Container component for popover menu with watchlist selection and creation.
 * Uses useSafeAreaInsets hook to get bottom inset for proper safe area handling.
 * @param root0 - Component props
 * @param root0.selectedWatchlist - Currently selected watchlist to highlight
 * @param root0.watchlists - Array of available watchlists to display in menu
 * @param root0.onSelect - Callback invoked when watchlist is selected with watchlist ID
 * @param root0.onCallbackOpen - Callback invoked when popover menu opens
 * @param root0.onOpenMyRoster - Callback invoked when "My Roster" option is selected
 */
const PopoverMenuContainer = ({
  selectedWatchlist,
  watchlists,
  onSelect,
  onCallbackOpen,
  onOpenMyRoster,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onCallbackOpen?.();
  }, [onCallbackOpen]);

  const handleWatchlistCreated = useCallback(
    (watchlist: WatchlistWatchlistMetaFragment) => {
      onSelect(watchlist.id);
      handleClose();
    },
    [handleClose, onSelect]
  );

  const { handleAddWatchlistPress, loading } = useCreateWatchlistActions({
    onCreate: handleWatchlistCreated,
  });

  const [showActionsheet, setShowActionsheet] = useState(false);

  const handleActionsheetClose = useCallback(
    () => setShowActionsheet(false),
    []
  );

  const handleActionsheetOpen = useCallback(() => {
    handleClose();
    setShowActionsheet(true);
  }, [handleClose]);

  const handleActionAddWatchlist = useCallback(() => {
    handleClose();
    handleAddWatchlistPress();
  }, [handleClose, handleAddWatchlistPress]);

  // Rows for the native MobileActionMenu (SE-5546): the watchlists (current
  // selection carries a check via the icon slot), My Roster, New List, and
  // Manage Lists. The menu molecule closes the sheet BEFORE firing onPress,
  // so handlers that also call handleClose stay harmless no-ops on re-close.
  const nativeMenuItems = useMemo<readonly MobileActionMenuItem[]>(
    () => [
      ...watchlists.map(watchlist => ({
        icon: watchlist.id === selectedWatchlist?.id ? CheckIcon : undefined,
        label: watchlist.name,
        onPress: () => onSelect(watchlist.id),
      })),
      {
        icon: selectedWatchlist == null ? CheckIcon : undefined,
        label: "My Roster",
        onPress: () => onOpenMyRoster?.(),
      },
      {
        icon: PlusIcon,
        label: loading ? "Please Wait..." : "New List",
        disabled: loading,
        testID: TEST_ID_WATCHLIST.NEW_LIST_BUTTON,
        onPress: handleActionAddWatchlist,
      },
      {
        icon: SettingsIcon,
        label: "Manage Lists",
        onPress: handleActionsheetOpen,
      },
    ],
    [
      watchlists,
      selectedWatchlist,
      onSelect,
      onOpenMyRoster,
      loading,
      handleActionAddWatchlist,
      handleActionsheetOpen,
    ]
  );

  const { width, height } = useWindowDimensions();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Limit dropdown height to 60% of screen to prevent viewport overflow on mobile
  const maxDropdownHeight = height * 0.6;

  const renderTrigger = useMemo(() => {
    /**
     * Render function for popover trigger button with watchlist name
     * @param triggerProps - Props passed from Popover to trigger element
     */
    function PopoverMenuTrigger(
      triggerProps: Record<string, unknown>
    ): React.JSX.Element {
      return (
        <Button
          {...triggerProps}
          testID={TEST_ID_WATCHLIST.POPOVER_OPEN_BUTTON}
          className="flex-1 gap-1"
          variant="link"
        >
          <ButtonText
            className="max-w-48 font-heading text-title text-status-info"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {selectedWatchlist?.name ?? "My Roster"}
          </ButtonText>
          <ButtonIcon
            as={ChevronsUpDownIcon}
            className="text-status-info"
            size="md"
          />
        </Button>
      );
    }
    return PopoverMenuTrigger;
  }, [selectedWatchlist?.name]);

  return (
    <PopoverMenuView
      showActionsheet={showActionsheet}
      onActionsheetClose={handleActionsheetClose}
      selectedWatchlist={selectedWatchlist}
      watchlists={watchlists}
      onSelect={onSelect}
      isOpen={isOpen}
      widthName={width}
      maxHeight={maxDropdownHeight}
      onClose={handleClose}
      onOpen={handleOpen}
      onAddWatchlistPress={handleActionAddWatchlist}
      loading={loading}
      onActionsheetOpen={handleActionsheetOpen}
      isDark={isDark}
      onMyRosterOpen={onOpenMyRoster}
      bottomInset={bottomInset}
      renderTrigger={renderTrigger}
      nativeMenuItems={nativeMenuItems}
    />
  );
};

export default PopoverMenuContainer;
